<template>
  <PremiumMetricWidgetBase :widget="widget" v-slot="{ metric }" :state="state">
    <div class="chart-wrapper">
      <apexchart height="100%" width="100%" :options="chartOptions" :series="series(metric)" />
    </div>
  </PremiumMetricWidgetBase>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import dayjs from 'dayjs';
import PremiumMetricWidgetBase from './PremiumMetricWidgetBase.vue';
import { formatNumber } from '@/utils/formatNumbers';
import {
  getChartExportSettings,
  getDefaultChartOptions,
  type BaseChartOptions,
} from '@/utils/chartSettings';
import { normalizeDateForTimeComparison } from '@/utils/dateUtils';
import type { WidgetFilter, WidgetObject, WidgetSetting } from '@/types/Widget';

const props = defineProps<{
  widget: WidgetObject;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  cluster?: WidgetSetting;
  day?: WidgetSetting;
}>();

const state = ref<string | null>(null);

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const series = (metric: any) => {
  const firstDate = metric?.data?.data?.arrivals[0]?.time_arrivals[0]?.date;
  if (!firstDate) {
    state.value = 'disabled';
    return [];
  }

  return (
    metric?.data?.data?.arrivals
      .sort((a: any, b: any) => a.time_arrivals[0].date.localeCompare(b.time_arrivals[0].date))
      .map((item: any) => {
        const itemDate = item.time_arrivals[0].date;
        return {
          name: new Date(itemDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            weekday: 'short',
          }),
          data: item.time_arrivals
            // .sort((a: any, b: any) => a.time.localeCompare(b.time))
            .map((time: any) => ({
              /* The normalizeDateForTimeComparison function normalizes dates so that times can be compared across different days.
               It calculates the day difference between the first date and the current item date,
               then creates a new date by adding that difference to the first date.
               This allows times to be overlaid on the same chart for comparison.
            */
              x: new Date(
                normalizeDateForTimeComparison(firstDate, itemDate, time.date) + ' ' + time.time,
              ).getTime(),
              y: time.total_arrival_pct,
            })),
        };
      }) ?? []
  );
};

const chartOptions = ref<BaseChartOptions>({
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'line',
    toolbar: getChartExportSettings(
      props.widget.name,
      props.area?.name || '',
      props.time?.name || '',
      props.zone?.title || '',
      props.cluster?.title || '',
      props.day?.title || '',
    ),
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  labels: [],
  xaxis: {
    type: 'datetime',
    tooltip: {
      enabled: false,
    },
    labels: {
      hideOverlappingLabels: true,
      rotate: 0,
      format: 'HH:mm',
    },
  },
  yaxis: {
    show: true,
    labels: {
      formatter: (val: number) => `${formatNumber(100 * val, 2)}%`,
    },
  },
  tooltip: {
    x: {
      formatter: (val: string) => dayjs(val).format('HH:mm'),
    },
    y: {
      formatter: (val: number) => `${formatNumber(100 * val, 2)}%`,
    },
  },
  legend: {
    ...defaultChartOptions.value.legend,
    horizontalAlign: 'center',
  },
});
</script>
